/**
 * Ecosystem Insights — Phase 5 unified dashboard.
 *
 * Three surfaces:
 *   1. Deal Recommendations (Iqra-narrated)
 *   2. Market Pulse — anonymised cross-tenant aggregates
 *   3. Tenant opt-in toggle for contributing to market pulse
 *
 * Risk Pulse is rendered inline inside NodePanel.jsx when a node is open
 * (not a separate page) — that's the right context for "risk for THIS entity".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, TrendingUp, BarChart3, Building2, Users, ArrowRight, Globe2, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';

export default function EcosystemInsights() {
  const { effective } = useTheme();
  const isDark = effective === 'dark';
  const [recs, setRecs] = useState([]);
  const [market, setMarket] = useState(null);
  const [optIn, setOptIn] = useState(true);
  const [tab, setTab] = useState('recs'); // 'recs' | 'market'
  const [recsLoading, setRecsLoading] = useState(true);
  const [marketLoading, setMarketLoading] = useState(true);

  useEffect(() => {
    api.get('/insights/recommendations?limit=12')
      .then(({ data }) => setRecs(data.recommendations || []))
      .catch(() => toast.error('Failed to load recommendations'))
      .finally(() => setRecsLoading(false));
    api.get('/insights/market-pulse')
      .then(({ data }) => setMarket(data))
      .catch(() => toast.error('Failed to load market pulse'))
      .finally(() => setMarketLoading(false));
  }, []);

  const toggleOptIn = async () => {
    try {
      const { data } = await api.put('/insights/market-share-opt', { share: !optIn });
      setOptIn(data.share_market_data);
      toast.success(data.share_market_data ? 'Now contributing to Market Pulse.' : 'Opted out of Market Pulse.');
    } catch {
      toast.error('Could not update preference.');
    }
  };

  const gold = isDark ? '#8B3FC5' : '#5E1F8B';
  const cardCls = isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200 shadow-sm';
  const subCls  = isDark ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-6" data-testid="ecosystem-insights-page">
      {/* Header */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
             style={{ backgroundColor: `${gold}1a`, color: gold }}>
          <Sparkles size={22} />
        </div>
        <div className="flex-1 min-w-[300px]">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: gold }}>
            Ecosystem Intelligence · Phase 5
          </div>
          <h1 className={`display text-2xl md:text-3xl mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
            Recommendations · Market Pulse · Risk
          </h1>
          <p className={`text-sm mt-1 max-w-2xl ${subCls}`}>
            Iqra surfaces next-best deals from your graph position and ecosystem velocity.
            Yusuf monitors entity-level risk in the side panel. Market Pulse anonymises
            ecosystem-wide benchmarks across opted-in tenants.
          </p>
        </div>
        <button
          onClick={toggleOptIn}
          data-testid="ecosystem-optin-toggle"
          className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] rounded-lg border flex items-center gap-1.5 ${
            optIn
              ? (isDark ? 'border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10'
                       : 'border-emerald-500/40 text-emerald-700 hover:bg-emerald-50')
              : (isDark ? 'border-white/10 text-slate-300 hover:bg-white/5'
                       : 'border-slate-200 text-slate-600 hover:bg-slate-50')
          }`}
        >
          <Globe2 size={12} />
          {optIn ? 'Contributing to Market Pulse' : 'Not contributing'}
        </button>
      </div>

      {/* Tab strip */}
      <div className={`flex items-center gap-1 p-1 rounded-xl border w-fit ${
        isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-slate-200'
      }`}>
        <TabBtn active={tab === 'recs'} onClick={() => setTab('recs')} isDark={isDark} testId="insights-tab-recs">
          <ArrowRight size={13} /> Deal Recommendations
        </TabBtn>
        <TabBtn active={tab === 'market'} onClick={() => setTab('market')} isDark={isDark} testId="insights-tab-market">
          <BarChart3 size={13} /> Market Pulse
        </TabBtn>
      </div>

      {tab === 'recs' && (
        <RecsPanel recs={recs} loading={recsLoading} isDark={isDark} gold={gold} cardCls={cardCls} subCls={subCls} />
      )}
      {tab === 'market' && (
        <MarketPanel market={market} loading={marketLoading} isDark={isDark} gold={gold} cardCls={cardCls} subCls={subCls} />
      )}
    </div>
  );
}

function RecsPanel({ recs, loading, isDark, gold, cardCls, subCls }) {
  if (loading) return <div className={subCls}>Loading recommendations…</div>;
  if (recs.length === 0) {
    return (
      <div className={`rounded-2xl border p-8 text-center ${cardCls}`} data-testid="insights-recs-empty">
        <div className={`text-sm ${subCls}`}>
          No recommendations yet — Iqra needs more graph density to surface high-confidence matches.
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="insights-recs-grid">
      {recs.map((r, i) => (
        <div key={i}
             className={`rounded-2xl border p-4 hover:scale-[1.005] transition-transform ${cardCls}`}
             data-testid={`insights-rec-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: `${gold}1a`, color: gold }}>
              Score {r.score}
            </span>
            <span className={`text-[9px] uppercase tracking-[0.18em] ${subCls}`}>
              {r.kind.replace(/_/g, ' ')}
            </span>
          </div>
          <div className={`flex items-center gap-2 mb-1 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
            <span className="font-semibold text-sm">{r.from.label}</span>
            <ArrowRight size={12} className={subCls} />
            <span className="font-semibold text-sm">{r.to.label}</span>
          </div>
          <p className={`text-xs leading-relaxed mt-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
            “{r.rationale}”
          </p>
        </div>
      ))}
    </div>
  );
}

function MarketPanel({ market, loading, isDark, gold, cardCls, subCls }) {
  const total_sectors = market?.top_sectors_by_startups?.length || 0;
  const total_hot     = market?.hot_sectors_by_recent_deals?.length || 0;

  if (loading) return <div className={subCls}>Loading market pulse…</div>;
  if (!market) return <div className={subCls}>Market pulse unavailable.</div>;

  return (
    <div className="space-y-4" data-testid="insights-market-grid">
      {/* Top-line stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={<Users size={14} />} label="Active investors (180d)" value={market.active_investor_count} isDark={isDark} gold={gold} />
        <MetricCard icon={<Building2 size={14} />} label="Sectors tracked" value={total_sectors} isDark={isDark} gold={gold} />
        <MetricCard icon={<TrendingUp size={14} />} label="Hot sectors (90d)" value={total_hot} isDark={isDark} gold={gold} />
        <MetricCard icon={<ShieldCheck size={14} />} label="Anonymised" value="Yes" isDark={isDark} gold={gold} />
      </div>

      {/* Sector tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SectorList
          title="Top sectors by startup count"
          items={(market.top_sectors_by_startups || []).map((x) => ({ name: x.sector, value: x.count, unit: 'startups' }))}
          isDark={isDark} gold={gold} cardCls={cardCls} subCls={subCls}
        />
        <SectorList
          title="Hot sectors · last 90 days"
          items={(market.hot_sectors_by_recent_deals || []).map((x) => ({ name: x.sector, value: x.deals_90d, unit: 'deals' }))}
          isDark={isDark} gold={gold} cardCls={cardCls} subCls={subCls}
        />
      </div>

      {/* Stage mix */}
      {(market.stage_mix || []).length > 0 && (
        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <div className={`text-[10px] uppercase tracking-[0.18em] mb-3 ${subCls}`}>Stage mix</div>
          <StageBar mix={market.stage_mix} isDark={isDark} />
        </div>
      )}

      {/* Deal size buckets */}
      {(market.deal_size_buckets || []).length > 0 && (
        <div className={`rounded-2xl border p-4 ${cardCls}`}>
          <div className={`text-[10px] uppercase tracking-[0.18em] mb-3 ${subCls}`}>Deal size distribution (per sector)</div>
          <div className="space-y-3">
            {market.deal_size_buckets.map((s) => (
              <div key={s.sector}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>{s.sector}</span>
                  <span className={`text-[10px] ${subCls}`}>{s.total_deals} deals</span>
                </div>
                <DealBuckets buckets={s.buckets} total={s.total_deals} isDark={isDark} gold={gold} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`text-[10px] ${subCls} pl-1`}>
        All values aggregated across opted-in tenants.  Individual deals, entity names, and tenant IDs are never exposed.
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, isDark, gold }) {
  return (
    <div className={`p-3 rounded-xl border ${isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-1.5 mb-2" style={{ color: gold }}>
        {icon}
        <span className="text-[9px] uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className={`display text-xl tabular-nums ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>{value}</div>
    </div>
  );
}

function SectorList({ title, items, isDark, gold, cardCls, subCls }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className={`rounded-2xl border p-4 ${cardCls}`}>
      <div className={`text-[10px] uppercase tracking-[0.18em] mb-3 ${subCls}`}>{title}</div>
      <div className="space-y-2">
        {items.length === 0 && <div className={`text-xs ${subCls}`}>—</div>}
        {items.map((it) => (
          <div key={it.name}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>{it.name}</span>
              <span className={`tabular-nums ${subCls}`}>{it.value} {it.unit}</span>
            </div>
            <div className={`h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
              <div className="h-full rounded-full" style={{ width: `${(it.value / max) * 100}%`, backgroundColor: gold }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageBar({ mix, isDark }) {
  const total = mix.reduce((acc, s) => acc + s.count, 0) || 1;
  const palette = ['#8B3FC5', '#3B82F6', '#7C3AED', '#10B981', '#14B8A6', '#06B6D4'];
  return (
    <>
      <div className="flex h-2.5 rounded-full overflow-hidden">
        {mix.map((s, i) => (
          <div key={s.stage} style={{ width: `${(s.count / total) * 100}%`, backgroundColor: palette[i % palette.length] }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[10px]">
        {mix.map((s, i) => (
          <div key={s.stage} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: palette[i % palette.length] }} />
            <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{s.stage}</span>
            <span className={`tabular-nums ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>· {s.count}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function DealBuckets({ buckets, total, isDark, gold }) {
  const max = total || 1;
  return (
    <div className="grid grid-cols-4 gap-2">
      {buckets.map((b) => (
        <div key={b.label} className={`p-2 rounded-md border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-slate-50 border-slate-200'}`}>
          <div className={`text-[9px] uppercase tracking-[0.15em] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{b.label}</div>
          <div className={`text-sm tabular-nums mt-0.5 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>{b.count}</div>
          <div className={`h-1 rounded mt-1 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
            <div className="h-full rounded" style={{ width: `${(b.count / max) * 100}%`, backgroundColor: gold }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TabBtn({ active, onClick, isDark, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold rounded-lg flex items-center gap-1.5 transition-colors ${
        active
          ? (isDark ? 'bg-[#8B3FC5] text-[#0A0A0A]' : 'bg-[#5E1F8B] text-white')
          : (isDark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-100')
      }`}
    >
      {children}
    </button>
  );
}
